import React, { useState, useCallback } from 'react';
import { Agent } from '../../../types';
import { Button } from '../../common';
import MobileAgentCard from './MobileAgentCard';
import KGAgentProgress from './KGAgentProgress';
import { KGImportProgressTracker } from '../KnowledgeGraphBuild/KGImportProgressTracker';
import { useKGImportProgressContext } from '../../../contexts/KGImportProgressContext';
import { useAdminTranslation } from '../../../modules/i18n';
import { XMarkIcon, EyeSlashIcon, EyeIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { getAgentStatusDisplay } from '../../../utils/agentStatusUtils';
import { getAgentWelcomeMessage } from '../../../utils/agentUtils';

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('vi-VN', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
};

interface AgentsContentProps {
  agents: Agent[];
  showHidden: boolean;
  onShowHiddenChange: (show: boolean) => void;
  onCreateAgent: () => void;
  onEditAgent: (agent: Agent) => void;
  onDeleteAgent: (agentId: string) => void;
  onViewAgentDetail: (agent: Agent) => void;
  onToggleHidden: (agentId: string) => void;
}

const AgentsContent: React.FC<AgentsContentProps> = ({
  agents,
  showHidden,
  onShowHiddenChange,
  onCreateAgent,
  onEditAgent,
  onDeleteAgent,
  onViewAgentDetail,
  onToggleHidden
}) => {
  const { t } = useAdminTranslation();
  const { startTracking } = useKGImportProgressContext();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  // Handle viewing detailed progress modal
  const handleViewProgress = useCallback(
    (jobId: string) => {
      startTracking(jobId);
      setSelectedJobId(jobId);
    },
    [startTracking]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
  }, []);

  return (
    <>
    <div className='flex h-full flex-col gap-6'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h2 className='text-lg font-medium text-gray-900 dark:text-dark-text'>{t('content.agents.title')}</h2>
          <p className='mt-1 text-sm text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.description')}</p>
        </div>
        <div className='mt-4 flex items-center space-x-3 sm:mt-0'>
          {/* Show Hidden Toggle */}
          <label className='flex cursor-pointer items-center space-x-2'>
            <input
              type='checkbox'
              checked={showHidden}
              onChange={(e) => onShowHiddenChange(e.target.checked)}
              className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-border-dark dark:bg-dark-background-tertiary'
            />
            <span className='text-sm text-gray-600 dark:text-dark-text-secondary'>{t('content.agents.showHidden')}</span>
          </label>
          <Button
            onClick={onCreateAgent}
            variant='primary'
            icon={
              <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
              </svg>
            }
          >
            {t('content.agents.createButton')}
          </Button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className='min-h-0 flex-1 overflow-hidden'>
        {agents.length === 0 ? (
          <div className='flex h-64 items-center justify-center'>
            <div className='text-center'>
              <svg className='mx-auto h-12 w-12 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                />
              </svg>
              <h3 className='mt-2 text-lg font-medium text-gray-900 dark:text-dark-text'>{t('content.agents.emptyState.title')}</h3>
              <p className='mt-1 text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.emptyState.description')}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className='hidden min-h-0 overflow-hidden rounded-lg border border-gray-200 dark:border-border-dark lg:block'>
              <div className='scrollbar-thin max-h-full overflow-auto'>
                <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                  <thead className='sticky top-0 z-10 bg-gray-50 shadow-sm dark:bg-dark-background-tertiary'>
                    <tr>
                      <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.agents.tableHeaders.agent')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.agents.tableHeaders.type')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.agents.tableHeaders.kgStatus')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.agents.tableHeaders.language')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.agents.tableHeaders.status')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.agents.tableHeaders.updated')}
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.agents.tableHeaders.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-dark-background-secondary'>
                    {agents.map((agent) => {
                      const statusDisplay = getAgentStatusDisplay(agent.status, t);
                      const isHidden = agent.hidden ?? false;
                      return (
                        <tr
                          key={agent.id}
                          className={`transition-colors hover:bg-gray-50 dark:hover:bg-dark-background-hover ${isHidden ? 'opacity-50' : ''}`}
                        >
                          <td className='whitespace-nowrap px-6 py-4'>
                            <div className='flex items-center'>
                              <div className='mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary dark:text-dark-text'>
                                <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className='text-sm font-medium text-gray-900 dark:text-dark-text'>{agent.name}</div>
                                <div className='max-w-xs truncate text-sm text-gray-500 dark:text-dark-text-secondary'>{getAgentWelcomeMessage(agent)}</div>
                              </div>
                            </div>
                          </td>
                          <td className='whitespace-nowrap px-6 py-4'>
                            {agent.agentType === 'DIRECT_QA' ? (
                              <span className='inline-flex rounded-md bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300'>
                                {t('content.agents.agentType.directQa')}
                              </span>
                            ) : (
                              <span className='inline-flex rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'>
                                {t('content.agents.agentType.kgRag')}
                              </span>
                            )}
                          </td>
                          <td className='whitespace-nowrap px-6 py-4'>
                            <KGAgentProgress agent={agent} onViewDetails={handleViewProgress} />
                          </td>
                          <td className='whitespace-nowrap px-6 py-4'>
                            <span className='inline-flex rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'>
                              {agent.language?.name || t('content.agents.status.unknown')}
                            </span>
                          </td>
                          <td className='whitespace-nowrap px-6 py-4'>
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusDisplay.className}`}>
                              {statusDisplay.text}
                            </span>
                          </td>
                          <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-dark-text-secondary'>{formatDate(agent.updatedAt)}</td>
                          <td className='whitespace-nowrap px-6 py-4 text-right text-sm font-medium'>
                            <button
                              onClick={() => onViewAgentDetail(agent)}
                              className='mr-3 rounded p-1 text-green-600 transition-colors hover:bg-green-50 hover:text-green-800 dark:text-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-300'
                              title={t('content.agents.actions.view')}
                            >
                              <InformationCircleIcon className='h-4 w-4' />
                            </button>
                            <button
                              onClick={() => onToggleHidden(agent.id)}
                              className={`mr-3 rounded p-1 transition-colors ${
                                isHidden
                                  ? 'text-amber-600 hover:bg-amber-50 hover:text-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/20 dark:hover:text-amber-300'
                                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text-secondary'
                              }`}
                              title={isHidden ? t('content.agents.actions.show') : t('content.agents.actions.hide')}
                            >
                              {isHidden ? <EyeIcon className='h-4 w-4' /> : <EyeSlashIcon className='h-4 w-4' />}
                            </button>
                            <button
                              onClick={() => onEditAgent(agent)}
                              className='mr-3 rounded p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800 dark:text-blue-400 dark:hover:bg-sky-400/50 dark:hover:text-blue-300'
                              title={t('content.agents.actions.edit')}
                            >
                              <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => onDeleteAgent(agent.id)}
                              className='rounded p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900 dark:text-red-400 dark:hover:bg-rose-400/50 dark:hover:text-red-300'
                              title={t('content.agents.actions.delete')}
                            >
                              <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                                />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className='h-full space-y-4 overflow-y-auto lg:hidden'>
              {agents.map((agent) => (
                <MobileAgentCard
                  key={agent.id}
                  agent={agent}
                  onEdit={() => onEditAgent(agent)}
                  onDelete={() => onDeleteAgent(agent.id)}
                  onViewDetail={() => onViewAgentDetail(agent)}
                  onViewProgress={handleViewProgress}
                  onToggleHidden={() => onToggleHidden(agent.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>

      {/* Progress Detail Modal */}
      {selectedJobId && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
          <div className='relative max-h-[90vh] w-full max-w-4xl overflow-auto rounded-xl bg-white shadow-2xl dark:bg-dark-background-secondary'>
            {/* Modal Header */}
            <div className='sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 dark:border-border-dark dark:bg-dark-background-secondary'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-dark-text'>
                {t('content.agents.workflowProgress.progressDetails')}
              </h3>
              <button
                onClick={handleCloseModal}
                className='rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-background-hover dark:hover:text-dark-text-secondary'
              >
                <XMarkIcon className='h-6 w-6' />
              </button>
            </div>

            {/* Modal Body */}
            <div className='p-6'>
              <KGImportProgressTracker jobId={selectedJobId} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentsContent;
